import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {produce} from 'immer'
import {useEffect, useEffectEvent, useRef} from 'react'

import {
  mutationLifecycleEventTarget,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
  type UpdateObjectPropertiesMutationEvent,
} from './mutation-lifecycle-events'

/**
 * Walks every nested object/array in `data` looking for a node whose `id`
 * matches `id`. For each matching node, applies every `key`/`value` pair in
 * `values` — but only when the node already has that key as its own property,
 * so a node whose GraphQL selection didn't fetch a given field (e.g. a node
 * that only fetches `titleHTML`, not `title`) is left untouched for that field.
 *
 * When both `node[key]` and `value` are plain objects, merge recursively and
 * only overwrite keys present in `value`; keys not present in `value` are
 * preserved. For primitives, arrays, and non-plain objects, `node[key]` is
 * replaced directly.
 *
 * The same id can legitimately appear more than once in a single document
 * (e.g. an issue referenced by both `blockedBy` and `subIssues`), so
 * traversal never stops after the first match.
 *
 * Also returns the pre-patch value of each key the first time it's
 * encountered (across every location `id` appears), as an `ObjectPropertiesPatch`
 * that — if applied back through this same function — reverts just this
 * patch's fields, e.g. to build an inverse patch that rolls back one mutation's
 * change without disturbing fields any other mutation may have since written
 * to the same document.
 *
 * Returns the same `data` reference unchanged if nothing matched (immer's
 * structural sharing), so unrelated cache entries don't get a spurious new
 * identity/re-render.
 */
function patchObjectById<T>(
  data: T,
  id: string,
  values: Record<string, unknown>,
): {data: T; previousPatch: ObjectPropertiesPatch} {
  const previousValues: Record<string, unknown> = {}

  function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false

    const prototype = Object.getPrototypeOf(value)
    return prototype === null || prototype === Object.prototype
  }

  function deepMergePlainObjects(
    target: Record<string, unknown>,
    source: Record<string, unknown>,
  ): Record<string, unknown> {
    const merged: Record<string, unknown> = {...target}

    for (const key of Object.keys(source)) {
      const sourceValue = source[key]
      const targetValue = merged[key]

      if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
        merged[key] = deepMergePlainObjects(targetValue, sourceValue)
        continue
      }

      merged[key] = sourceValue
    }

    return merged
  }

  function cloneRollbackValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(cloneRollbackValue)
    }

    if (isPlainObject(value)) {
      const clone: Record<string, unknown> = {}
      for (const key of Object.keys(value)) {
        clone[key] = cloneRollbackValue(value[key])
      }
      return clone
    }

    return value
  }

  if (!data) return {data, previousPatch: {id, values: previousValues}}

  const nextData = produce(data, draft => {
    visit(draft)
  })

  return {data: nextData, previousPatch: {id, values: previousValues}}

  function visit(node: unknown): void {
    if (Array.isArray(node)) {
      for (const item of node) visit(item)
      return
    }

    if (!node || typeof node !== 'object') return

    const obj = node as Record<string, unknown>

    if (obj.id === id) {
      for (const key of Object.keys(values)) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          if (!(key in previousValues)) {
            previousValues[key] = cloneRollbackValue(obj[key])
          }

          const nextValue = values[key]
          if (isPlainObject(obj[key]) && isPlainObject(nextValue)) {
            obj[key] = deepMergePlainObjects(obj[key], nextValue)
            continue
          }

          obj[key] = nextValue
        }
      }
    }

    for (const value of Object.values(obj)) {
      visit(value)
    }
  }
}

/**
 * Keeps the TanStack Query cache entry at `queryKey` in sync with any mutation
 * that dispatches `UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT` on
 * `mutationLifecycleEventTarget` instead of writing into this cache directly
 * (the mutation has no knowledge of the caller's TSQ query key).
 *
 * Pass `enabled: false` to skip subscribing (e.g. while a Relay-backed path is
 * active instead of the TanStack-backed one).
 *
 * `T` is the shape of the cached document (the caller's query data type) —
 * it's not inferable from the arguments below, so callers must supply it
 * explicitly, e.g. `useSyncObjectPropertiesPatchToQueryCache<MyQuery['repository']>(...)`.
 */
export function useSyncObjectPropertiesPatchToQueryCache<T>({
  queryKey,
  enabled,
}: {
  queryKey: QueryKey
  enabled: boolean
}) {
  const queryClient = useQueryClient()
  // The inverse of each mutation's optimistic patch (its patched fields' pre-patch
  // values), keyed by `mutationId` so a subsequent 'error' event can revert only
  // that mutation's own fields — not the whole document — on rollback. Keying by
  // `mutationId` (rather than a single shared ref, or a whole-document snapshot)
  // matters because multiple mutations can be in flight concurrently:
  //   - an 'error'/'completed' event for one mutation must never clear or roll
  //     back the pending state of a different, unrelated one, and
  //   - rolling back one mutation's fields must not clobber fields a different
  //     mutation has already confirmed on the same document in the meantime.
  // Entries are removed once their mutation completes or errors.
  const pendingOptimisticPatchesRef = useRef(new Map<string, ObjectPropertiesPatch>())

  const handleMutationEvent = useEffectEvent((event: Event) => {
    const {detail} = event as UpdateObjectPropertiesMutationEvent
    const {mutationId} = detail
    const pendingOptimisticPatches = pendingOptimisticPatchesRef.current

    if (detail.phase === 'error') {
      const inversePatch = pendingOptimisticPatches.get(mutationId)
      if (inversePatch) {
        pendingOptimisticPatches.delete(mutationId)
        queryClient.setQueryData<T>(queryKey, old => patchObjectById(old, inversePatch.id, inversePatch.values).data)
      }
      return
    }

    const {id, values} = detail.data

    if (detail.phase === 'optimistic') {
      queryClient.setQueryData<T>(queryKey, old => {
        const {data, previousPatch} = patchObjectById(old, id, values)
        pendingOptimisticPatches.set(mutationId, previousPatch)
        return data
      })
      return
    }

    // Mutation succeeded — write the server-confirmed data (not the earlier
    // optimistic snapshot); no rollback needed anymore for this mutation.
    pendingOptimisticPatches.delete(mutationId)
    queryClient.setQueryData<T>(queryKey, old => patchObjectById(old, id, values).data)
  })

  // `queryKey` is an array, so it's compared by its stable hash rather than by reference.
  const queryKeyHash = hashKey(queryKey)

  useEffect(() => {
    if (!enabled) return

    const handleEvent = (event: Event) => handleMutationEvent(event)

    const controller = new AbortController()
    mutationLifecycleEventTarget.addEventListener(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, handleEvent, {
      signal: controller.signal,
    })
    return () => controller.abort()
  }, [enabled, queryKeyHash, queryClient])
}
